import AppPageTitle from '@/components/AppPageTitle';
import AppRangePicker from '@/components/AppRangePicker';
import AppSearch from '@/components/AppSearch';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import useMediaQuery, { mediaQueryPoints } from '@/hooks/useMediaQuery';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import { getSendSmsOverview } from '@/services/send_sms.service';
import { formatDate } from '@/utils/formatters';
import { EyeOutlined } from '@ant-design/icons';
import { render } from '@testing-library/react';
import { Button, Col, Flex, Row, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import { isArray } from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CreateSendSMSModal from './CreateSendSMSModal';

const START_DATE = dayjs().startOf('month').format('YYYY-MM-DD');
const END_DATE = dayjs().format('YYYY-MM-DD');

function SendSMS() {
  const navigate = useNavigate();
  const isBreakpoint = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const offset = (pageIndex - 1) * pageSize + 1;
  const [time, setTime] = useState([START_DATE, END_DATE]);
  const [searchTitleValue, setSearchTitleValue] = useState(null);
  const [searchIsdnListValue, setSearchIsdnListValue] = useState(null);
  const [query, setQuery] = useState({
    title: null,
    isdn_list: null,
    start_date: time[0],
    end_date: time[1],
  });
  const [openModal, setOpenModal] = useState(false);

  const { data, isLoading, refetch, isRefetching } = usePaginatedQuery(
    () =>
      getSendSmsOverview({
        ...query,
        limit: pageSize,
        offset: offset,
      }),
    ['send-sms-overview', query, pageSize, offset]
  );

  const handleQuery = () => {
    setQuery({
      title: searchTitleValue,
      isdn_list: searchIsdnListValue,
      start_date: time[0],
      end_date: time[1],
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setQuery({
      title: null,
      isdn_list: null,
    });
    setSearchIsdnListValue(null);
    setSearchTitleValue(null);
    setTime([START_DATE, END_DATE]);
    setPageIndex(1);
  };

  const columns = [
    {
      title: 'No.',
      dataIndex: 'no',
      key: 'no',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => (text ? text : '-'),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => (text ? formatDate(text) : '-'),
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      render: (text) => (text ? text : '-'),
    },
    {
      title: 'ISDN List',
      dataIndex: 'isdn_list',
      key: 'isdn_list',
      render: (text) => {
        const isdnList = text.split(',');
        if (isdnList.length === 0) return '-';
        return (
          <Row gutter={[8, 8]} className="justify-center">
            {isdnList.map((isdn, index) => (
              <Col key={index} span={10}>
                <Tag color="blue" className="text-center w-full mr-0">
                  {isdn.trim()}
                </Tag>
              </Col>
            ))}
          </Row>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: isBreakpoint ? 70 : 150,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleNavigate(record.id)}
          icon={isBreakpoint ? <EyeOutlined /> : null}
        >
          {isBreakpoint ? null : 'View Details'}
        </Button>
      ),
    },
  ];

  const handleNavigate = (id) => {
    navigate(PATH.SEND_SMS.DETAIL.replace(':id', id));
  };

  const handleOnOk = (dates, formatted) => {
    if (dates) {
      if (formatted) {
        setTime(formatted);
      }
    } else {
      setTime(null);
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoints.md}px)`);

  return (
    <Space direction="vertical" className="w-full">
      <AppPageTitle title="Send SMS" />
      <AppTableQuery handleQuery={handleQuery} handleReset={handleReset}>
        <Row gutter={[8, 8]} className="w-full">
          <Col span={isMobile ? 24 : 6}>
            <AppSearch
              label="Title"
              placeholder="Search Title"
              value={searchTitleValue}
              onChange={(val) => {
                setSearchTitleValue(val);
              }}
              className="w-full"
              formItemClassName={'mb-0'}
            />
          </Col>
          <Col span={isMobile ? 24 : 6}>
            <AppSearch
              label="ISDN List"
              placeholder="Search ISDN List"
              value={searchIsdnListValue}
              onChange={(val) => {
                setSearchIsdnListValue(val);
              }}
              className="w-full"
              formItemClassName={'mb-0'}
            />
          </Col>
          <Col span={isMobile ? 24 : 8}>
            <AppRangePicker
              date={isArray(time) && time.length === 2 ? time : null}
              onOk={handleOnOk}
              dateLimit={{
                value: 1,
                unit: 'year',
              }}
              label="Time"
              formItemClassName={'mb-0'}
            />
          </Col>
        </Row>
      </AppTableQuery>

      <AppTable
        columns={columns}
        dataSource={data?.data}
        totalItems={data?.total}
        scroll={{ x: 1200 }}
        loading={isLoading || isRefetching}
        onRefresh={refetch}
        showSizeChanger
        onCreate={handleOpenModal}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
      />

      {openModal && (
        <CreateSendSMSModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          refetchOverviewData={refetch}
        />
      )}
    </Space>
  );
}

export default SendSMS;
